type MessageListener = (data: string) => void;

class WebSocketService {
  public socket: WebSocket | null;
  public url: string | null;
  public reconnectAttempts: number;
  public maxReconnectAttempts: number;
  public reconnectDelay: number;
  public reconnectTimeout: NodeJS.Timeout | null;
  public messageListeners: MessageListener[];
  public shouldReconnect: boolean;

  public constructor() {
    this.socket = null;
    this.url = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.reconnectTimeout = null;
    this.messageListeners = [];
    this.shouldReconnect = true;
  }

  /**
   * Connects to a WebSocket server
   *
   * @param url - The WebSocket URL to connect to
   */
  public connect(url: string): void {
    if (this.socket) {
      console.warn(
        ' Closing previous WebSocket connection before opening a new one.'
      );
      this.disconnect();
    }

    this.url = url;
    this.shouldReconnect = true;
    console.log('Connecting to WebSocket:', url);
    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      console.log('WebSocket Connected:', url);
    };

    this.socket.onerror = () => {};

    this.socket.onclose = () => {
      this.socket = null;
      if (this.shouldReconnect) {
        this.attemptReconnect();
      }
    };

    this.socket.onmessage = (event) => {
      // Check for binary data
      if (event.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          // readAsText always produces a string result, so this cast is safe
          const textResult = reader.result as string;
          if (textResult) {
            this.notifyListeners(textResult);
          }
        };
        reader.readAsText(event.data);
      } else {
        this.notifyListeners(event.data);
      }
    };
  }

  /**
   * Disconnects from the WebSocket server and cleans up
   */
  public disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      this.shouldReconnect = false;
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
      console.log('🔌 WebSocket Disconnected');
    }
  }

  /**
   * Adds a message listener callback
   *
   * @param callback - Function to call when a message is received
   */
  public onMessage(callback: MessageListener): void {
    if (!this.messageListeners.includes(callback)) {
      this.messageListeners.push(callback);
    }
  }

  /**
   * Removes a message listener callback
   *
   * @param callback - Function to remove from listeners
   */
  public removeMessageListener(callback: MessageListener): void {
    this.messageListeners = this.messageListeners.filter(
      (cb) => cb !== callback
    );
  }

  /**
   * Notifies all registered listeners with the received data
   *
   * @param data - The message data to send to listeners
   */
  private notifyListeners(data: string): void {
    this.messageListeners.forEach((callback) => callback(data));
  }

  /**
   * Attempts to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (
      !this.shouldReconnect ||
      this.reconnectAttempts >= this.maxReconnectAttempts
    ) {
      console.warn('Max reconnect attempts reached. Stopping.');
      return;
    }

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      10000
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect(this.url!);
    }, delay);
  }

  /**
   * Sends a message through the WebSocket connection
   *
   * @param message - The message object to send
   */
  public sendMessage(message: unknown): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('⚠ WebSocket is not connected! Cannot send message.');
    }
  }
}

export default new WebSocketService();
